/**
 * Hook to fetch document counts for multiple doctypes
 * Uses React Query for caching and deduplication
 */

import { useQuery } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";

// Extract doctype from href (e.g., "/list/Item%20Group" -> "Item Group")
export function extractDoctypeFromHref(href: string): string | null {
  const listMatch = href.match(/^\/list\/(.+)$/);
  if (listMatch) {
    return decodeURIComponent(listMatch[1]);
  }
  return null;
}

// Fetch count for a single doctype
async function fetchDoctypeCount(doctype: string): Promise<number> {
  try {
    const result = await frappeMethod<{ count: number }>(
      "frappe.client.get_count",
      { doctype }
    );
    return result?.count ?? 0;
  } catch {
    return 0;
  }
}

// Fetch counts for multiple doctypes
async function fetchDoctypeCounts(doctypes: string[]): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};

  // Fetch in parallel with a limit to avoid overwhelming the server
  const batchSize = 5;
  for (let i = 0; i < doctypes.length; i += batchSize) {
    const batch = doctypes.slice(i, i + batchSize);
    const results = await Promise.all(
      batch.map(async (doctype) => ({
        doctype,
        count: await fetchDoctypeCount(doctype),
      }))
    );
    results.forEach((r) => {
      counts[r.doctype] = r.count;
    });
  }

  return counts;
}

export function useDocTypeCounts(hrefs: string[]) {
  // Extract unique doctypes from hrefs
  const doctypes = [...new Set(
    hrefs
      .map(extractDoctypeFromHref)
      .filter((d): d is string => d !== null)
  )];

  return useQuery({
    queryKey: ["doctype-counts", doctypes.sort().join(",")],
    queryFn: () => fetchDoctypeCounts(doctypes),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    enabled: doctypes.length > 0,
    placeholderData: {} as Record<string, number>,
  });
}

// Get count for a specific href
export function getCountForHref(
  href: string,
  counts: Record<string, number> | undefined
): number | undefined {
  if (!counts) return undefined;
  const doctype = extractDoctypeFromHref(href);
  if (!doctype) return undefined;
  return counts[doctype];
}
